import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiOutlineCheck,
  HiOutlinePencilSquare,
  HiOutlineChatBubbleLeftEllipsis,
} from 'react-icons/hi2';
import SafeAvatar from '../../components/SafeAvatar';
import ChatAttachments, { ChatLightbox } from './ChatAttachments';
import './MemberActivityPopup.css';

/*
  멤버 활동 캘린더 팝업.
  - 활성 프로젝트의 categories → tasks 를 순회하며 이 멤버가 만든
    "체크리스트 체크 / 게시물 작성 / 댓글 작성" 활동을 모은다.
  - 캘린더에 활동 있는 날만 카운트 배지 표시, 선택일의 활동을 아래에 나열.
*/

// 본문 안의 http/https URL 을 클릭 가능한 링크로 변환 (Dashboard.js 의 renderTextWithLinks 와 동일)
function renderTextWithLinks(text) {
  if (!text || typeof text !== 'string') return text;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matchedUrlRegex = /^https?:\/\/[^\s]+$/;
  return text.split(urlRegex).map((part, index) =>
    matchedUrlRegex.test(part) ? (
      <a
        key={`link-${index}`}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}
      >
        {part}
      </a>
    ) : (
      <span key={`text-${index}`}>{part}</span>
    )
  );
}

function dayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildMonthCells(year, month) {
  const firstDow = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= lastDate; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function buildMemberActivities(project, memberId) {
  if (!project?.categories?.length || !memberId) return [];
  const out = [];
  project.categories.forEach((cat) => {
    (cat.tasks || []).forEach((task) => {
      (task.checklist || []).forEach((item) => {
        if (
          item.completed &&
          item.completedAt &&
          item.completedBy &&
          item.completedBy.id === memberId
        ) {
          const t = new Date(item.completedAt).getTime();
          if (Number.isNaN(t)) return;
          out.push({
            id: `cl-${item.id}`,
            type: 'checklist',
            at: item.completedAt,
            sortAt: t,
            taskName: task.name,
            categoryName: cat.name,
            body: item.text || '',
          });
        }
      });
      (task.posts || []).forEach((post) => {
        if (post.authorId === memberId && post.createdAt) {
          const t = new Date(post.createdAt).getTime();
          if (!Number.isNaN(t)) {
            out.push({
              id: `post-${post.id}`,
              type: 'post',
              at: post.createdAt,
              sortAt: t,
              taskName: task.name,
              categoryName: cat.name,
              body: post.content || '',
              attachments: Array.isArray(post.attachments) ? post.attachments : [],
              hiddenAt: post.hiddenAt || null,
            });
          }
        }
        (post.comments || []).forEach((cm) => {
          if (cm.authorId === memberId && cm.createdAt) {
            const t = new Date(cm.createdAt).getTime();
            if (Number.isNaN(t)) return;
            const snip = (post.content || '').trim().replace(/\s+/g, ' ').slice(0, 80);
            const parentSnip = (cm.parentSnippet || '').trim().replace(/\s+/g, ' ').slice(0, 80);
            out.push({
              id: `cm-${cm.id}`,
              type: 'comment',
              at: cm.createdAt,
              sortAt: t,
              taskName: task.name,
              categoryName: cat.name,
              postSnippet: snip,
              // 댓글에 단 답글이면 부모 댓글 정보로 '댓글:' 표시
              parentCommentId: cm.parentCommentId || null,
              parentSnippet: parentSnip,
              parentHiddenAt: cm.parentHiddenAt || null,
              body: cm.content || '',
              attachments: Array.isArray(cm.attachments) ? cm.attachments : [],
              hiddenAt: cm.hiddenAt || null,
            });
          }
        });
      });
    });
  });
  return out.sort((a, b) => b.sortAt - a.sortAt);
}

function ActivityTypeIcon({ type }) {
  if (type === 'checklist') return <HiOutlineCheck aria-hidden />;
  if (type === 'post') return <HiOutlinePencilSquare aria-hidden />;
  return <HiOutlineChatBubbleLeftEllipsis aria-hidden />;
}

export default function MemberActivityPopup({ member, project, isAdmin, isOnline, onClose, onSendDm }) {
  const { t } = useTranslation('memberActivity');
  const dow = t('dow', { returnObjects: true });
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(() => dayKey(today));
  const [lightbox, setLightbox] = useState(null);

  const activities = useMemo(
    () => buildMemberActivities(project, member?.id),
    [project, member?.id]
  );

  const activitiesByDay = useMemo(() => {
    const map = new Map();
    activities.forEach((a) => {
      const k = dayKey(new Date(a.at));
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    });
    return map;
  }, [activities]);

  const cells = useMemo(() => buildMonthCells(viewYear, viewMonth), [viewYear, viewMonth]);

  const todayKey = dayKey(today);
  const selectedActivities = activitiesByDay.get(selectedDay) || [];

  const goPrevMonth = () => {
    setSelectedDay(null);
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };
  const goNextMonth = () => {
    setSelectedDay(null);
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const formatSelectedHeader = () => {
    if (!selectedDay) return t('yearMonth', { y: viewYear, m: viewMonth + 1 });
    const [y, m, d] = selectedDay.split('-');
    return t('yearMonthDay', { y: Number(y), m: Number(m), d: Number(d) });
  };

  return (
    <div className="claude-popup-overlay claude-member-activity-overlay" onClick={onClose}>
      <div
        className="claude-popup-modal claude-member-activity-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="claude-popup-close" onClick={onClose} aria-label={t('close')}>
          ×
        </button>

        <div className="claude-member-activity-header">
          <span className="claude-presence-wrap">
            <SafeAvatar
              src={member?.avatar}
              alt="Profile"
              className="claude-team-avatar-img claude-team-avatar-sm"
              fallbackClassName="claude-team-avatar claude-team-avatar-sm"
              as="div"
            />
            {isOnline && <span className="claude-presence-dot" />}
          </span>
          <div className="claude-member-activity-name">
            <strong>{member?.name || t('noName')}</strong>
            <span>{t('summary', { n: activities.length })}</span>
          </div>
          {onSendDm && (
            <button
              type="button"
              className="claude-member-activity-dm-btn"
              onClick={() => {
                onSendDm(member?.id);
                onClose?.();
              }}
              title={t('sendDm')}
            >
              {t('sendDm')}
            </button>
          )}
        </div>

        <div className="claude-member-activity-scroll">
          <div className="claude-member-activity-calendar">
            <div className="claude-member-activity-cal-head">
              <button
                type="button"
                className="claude-member-activity-cal-nav"
                onClick={goPrevMonth}
                aria-label={t('prevMonth')}
              >
                ‹
              </button>
              <span className="claude-member-activity-cal-title">
                {t('yearMonth', { y: viewYear, m: viewMonth + 1 })}
              </span>
              <button
                type="button"
                className="claude-member-activity-cal-nav"
                onClick={goNextMonth}
                aria-label={t('nextMonth')}
              >
                ›
              </button>
            </div>
            <div className="claude-member-activity-cal-dow">
              {dow.map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>
            <div className="claude-member-activity-cal-grid">
              {cells.map((cell, i) => {
                if (!cell) {
                  return (
                    <div
                      key={`pad-${i}`}
                      className="claude-member-activity-cal-cell is-pad"
                      aria-hidden
                    />
                  );
                }
                const k = dayKey(cell);
                const dayActs = activitiesByDay.get(k);
                const isToday = k === todayKey;
                const isSelected = k === selectedDay;
                let checklistCount = 0;
                let postCount = 0;
                let commentCount = 0;
                if (dayActs) {
                  dayActs.forEach((a) => {
                    if (a.type === 'checklist') checklistCount++;
                    else if (a.type === 'post') postCount++;
                    else if (a.type === 'comment') commentCount++;
                  });
                }
                return (
                  <button
                    key={k}
                    type="button"
                    className={`claude-member-activity-cal-cell${isToday ? ' is-today' : ''}${
                      isSelected ? ' is-selected' : ''
                    }${dayActs ? ' has-activity' : ''}`}
                    onClick={() => setSelectedDay(k)}
                    aria-pressed={isSelected}
                  >
                    <span className="claude-member-activity-cal-num">{cell.getDate()}</span>
                    {checklistCount > 0 && (
                      <span
                        className="claude-member-activity-cal-count claude-member-activity-cal-count--checklist"
                        title={t('checklistCountTitle', { n: checklistCount })}
                      >
                        {checklistCount}
                      </span>
                    )}
                    {postCount > 0 && (
                      <span
                        className="claude-member-activity-cal-count claude-member-activity-cal-count--post"
                        title={t('postCountTitle', { n: postCount })}
                      >
                        {postCount}
                      </span>
                    )}
                    {commentCount > 0 && (
                      <span
                        className="claude-member-activity-cal-count claude-member-activity-cal-count--comment"
                        title={t('commentCountTitle', { n: commentCount })}
                      >
                        {commentCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="claude-member-activity-list">
            <h4 className="claude-member-activity-list-head">{formatSelectedHeader()}</h4>
            <div className="claude-member-activity-list-body">
              {!selectedDay ? (
                <p className="claude-member-activity-empty">{t('selectDayPrompt')}</p>
              ) : selectedActivities.length === 0 ? (
                <p className="claude-member-activity-empty">{t('noActivity')}</p>
              ) : (
                <ul>
                  {selectedActivities.map((a) => (
                    <li key={a.id} className={`claude-member-activity-item type-${a.type}`}>
                      <span className={`claude-member-activity-icon type-${a.type}`}>
                        <ActivityTypeIcon type={a.type} />
                      </span>
                      <div className="claude-member-activity-content">
                        <div className="claude-member-activity-meta">
                          <span className="claude-member-activity-type">
                            {t(`type.${a.type}`)}
                          </span>
                          <span className="claude-member-activity-dot">·</span>
                          <span>{a.categoryName}</span>
                          <span className="claude-member-activity-dot">·</span>
                          <span>{a.taskName}</span>
                          <span className="claude-member-activity-time">
                            {new Date(a.at).toLocaleTimeString('ko-KR', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false,
                            })}
                          </span>
                        </div>
                        {a.type === 'comment' &&
                          (a.parentCommentId ? (
                            <div className="claude-member-activity-quote">
                              {a.parentHiddenAt ? (
                                <>
                                  {t('commentLabel')} <em>{t('hiddenCommentQuote')}</em>
                                </>
                              ) : (
                                a.parentSnippet && (
                                  <>
                                    {t('commentLabel')} "{a.parentSnippet}"
                                  </>
                                )
                              )}
                            </div>
                          ) : (
                            a.postSnippet && (
                              <div className="claude-member-activity-quote">
                                {t('postLabel')} "{a.postSnippet}"
                              </div>
                            )
                          ))}
                        {a.hiddenAt ? (
                          isAdmin ? (
                            <>
                              <div className="claude-chat-hidden-banner">
                                {t(a.type === 'post' ? 'hiddenBanner.post' : 'hiddenBanner.comment')}
                              </div>
                              {a.body && (
                                <div className="claude-member-activity-body">
                                  {renderTextWithLinks(a.body)}
                                </div>
                              )}
                              {a.attachments && a.attachments.length > 0 && (
                                <div className="claude-member-activity-attachments">
                                  <ChatAttachments
                                    attachments={a.attachments}
                                    onOpenLightbox={(items, idx) =>
                                      setLightbox({ items, index: idx })
                                    }
                                  />
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="claude-member-activity-body claude-chat-hidden-placeholder">
                              {t(
                                a.type === 'post'
                                  ? 'hiddenPlaceholder.post'
                                  : 'hiddenPlaceholder.comment'
                              )}
                            </div>
                          )
                        ) : (
                          <>
                            {a.body && (
                              <div className="claude-member-activity-body">
                                {renderTextWithLinks(a.body)}
                              </div>
                            )}
                            {a.attachments && a.attachments.length > 0 && (
                              <div className="claude-member-activity-attachments">
                                <ChatAttachments
                                  attachments={a.attachments}
                                  onOpenLightbox={(items, idx) =>
                                    setLightbox({ items, index: idx })
                                  }
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      {lightbox && (
        <ChatLightbox
          items={lightbox.items}
          index={lightbox.index}
          onChangeIndex={(i) => setLightbox((cur) => (cur ? { ...cur, index: i } : cur))}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
